/**
 * Pure-function OIL date-eligibility validator.
 *
 * Extracted from `SmartLeaveApplicationModal.js` so the day-type rules
 * can be unit-tested without rendering the full modal.
 *
 * Three day-type outcomes per work-date:
 *   - 'weekend'              → eligible for all employees per their schedule.
 *   - 'public_holiday'       → eligible for all (with Saturday-PH MOM branch).
 *   - 'weekday_after_hours'  → eligible for non-workmen only (workmen receive
 *                              cash OT for weekday after-hours work under
 *                              MOM Part IV; OIL on top would double-compensate).
 *
 * See:
 *   - .cursor/rules/oil-weekday-after-hours-non-workman-only.mdc
 *   - staff_core/services/special_leave_service.py :: handle_off_in_lieu_claim_approval
 *     (backend mirror of the workman rejection)
 */
import { parseLocalISOToDate, toLocalISODate } from './dateUtils';

/**
 * @param {string} selectedDate - ISO date string (YYYY-MM-DD).
 * @param {object} opts
 * @param {Array<{holiday_date?: string, date?: string}>} opts.publicHolidays
 * @param {boolean} opts.is5HalfDaySchedule
 * @param {string} opts.employeeScheduleType  - one of '5_day' | '5_5_day' | '6_day'.
 * @param {boolean} opts.isWorker             - true if employment_classification === 'worker'.
 * @returns {{
 *   valid: boolean,
 *   message: string,
 *   restrict05?: boolean,
 *   forceFullDay?: boolean,
 *   isPublicHoliday?: boolean,
 *   isSaturdayPH?: boolean,
 *   isSaturday?: boolean,
 *   isSunday?: boolean,
 *   isWeekend?: boolean,
 *   isWeekdayAfterHours?: boolean,
 * }}
 */
export function validateOILDate(selectedDate, opts) {
  const {
    publicHolidays = [],
    is5HalfDaySchedule = false,
    employeeScheduleType = '5_day',
    isWorker = false,
  } = opts || {};

  if (!selectedDate) return { valid: false, message: '', restrict05: false };

  const date = parseLocalISOToDate(selectedDate);
  if (!date) return { valid: false, message: 'Invalid date', restrict05: false };

  const dayOfWeek = date.getDay(); // 0=Sun, 6=Sat
  const dateStr = toLocalISODate(date);

  const isPublicHoliday = publicHolidays.some(
    (ph) => ph.holiday_date === dateStr || ph.date === dateStr,
  );

  const isSaturday = dayOfWeek === 6;
  const isSunday = dayOfWeek === 0;

  const saturdayEnabled = is5HalfDaySchedule || employeeScheduleType === '6_day';

  // MOM COMPLIANCE: Saturday Public Holiday validation.
  if (isSaturday && isPublicHoliday) {
    if (saturdayEnabled) {
      return {
        valid: false,
        message:
          'Public holidays falling on Saturdays are not eligible for OIL credit for your work schedule per Singapore MOM regulations. The day is treated as a paid holiday.',
        restrict05: false,
      };
    }
    return {
      valid: true,
      isPublicHoliday: true,
      isSaturdayPH: true,
      forceFullDay: true,
      restrict05: false,
      message:
        'Public holiday on Saturday - eligible for 1.0 day OIL credit (MOM regulation for 5-day work week).',
    };
  }

  if (isPublicHoliday) {
    return {
      valid: true,
      isPublicHoliday: true,
      restrict05: false,
      message: 'Public holiday - eligible for Off in Lieu claim',
    };
  }

  if (is5HalfDaySchedule) {
    if (isSunday) {
      return {
        valid: true,
        isSunday: true,
        restrict05: false,
        message: 'Sunday - eligible for Off in Lieu claim',
      };
    }
    if (isSaturday) {
      return {
        valid: true,
        isSaturday: true,
        restrict05: true,
        message:
          'Saturday is a half work day for your schedule. You can claim 0.5 day for work done after 12:30pm.',
      };
    }
  } else {
    if (isSaturday || isSunday) {
      return {
        valid: true,
        isWeekend: true,
        restrict05: false,
        message: `${isSaturday ? 'Saturday' : 'Sunday'} - eligible for Off in Lieu claim`,
      };
    }
  }

  // Weekday after-hours work.
  //
  // Workmen are paid cash overtime under MOM Part IV for time past their
  // schedule on a working day; granting OIL on top would double-compensate
  // and is forbidden. Non-workmen (staff/managers/executives) do not
  // receive cash OT, so the company grants OIL for weekday after-hours
  // work as goodwill compensation. Backend mirror: SpecialLeaveService
  // .handle_off_in_lieu_claim_approval rejects workman weekday claims as
  // defense-in-depth.
  if (isWorker) {
    return {
      valid: false,
      message:
        'Selected date is a weekday. Workmen are compensated for weekday after-hours work via cash overtime, not OIL. Please ensure your overtime was declared at clock-out.',
      restrict05: false,
    };
  }

  return {
    valid: true,
    isWeekdayAfterHours: true,
    restrict05: false,
    message:
      'Weekday after-hours work - claim 0.5 day for a short late-stay or 1.0 day for a full extra day. Please describe the work performed below.',
  };
}
